import { FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';

// The delivery dimension (pretty name) to use: the selected one when this channel offers it,
// otherwise the channel's first. A preset can carry a dimension this channel doesn't have, and
// mapping that sent getChannelDelivery no `dimension` at all, which it rejects.
export const resolveDeliveryDimension = (
  selected: string | undefined,
  dimensionMap: Record<string, string>
): string => (selected && dimensionMap[selected] ? selected : Object.keys(dimensionMap)[0]);

// The filter a delivery request should carry: the working filter state/ID, or the page preset's,
// depending on where the filter currently comes from. Shared by the delivery fetch and the CSV
// export so both always describe the same slice of data.
interface DeliveryFilterInput {
  filterSource?: 'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState';
  filterState?: FilterPaneState;
  filterID?: number;
  presetFilterInfo?: { filterID?: number | null; filterState?: FilterPaneState | null };
}

export interface DeliveryFilter {
  filterID?: number;
  filterState?: FilterPaneState;
}

export const resolveDeliveryFilter = ({
  filterSource,
  filterState,
  filterID,
  presetFilterInfo
}: DeliveryFilterInput): DeliveryFilter => {
  if (filterSource === 'currentState' && filterState != null) return { filterState };
  if (filterSource === 'currentFilterID' && filterID != null) return { filterID };
  if (filterSource === 'presetFilterID' && presetFilterInfo?.filterID != null) {
    return { filterID: presetFilterInfo.filterID };
  }
  if (filterSource === 'presetState' && presetFilterInfo?.filterState) {
    return { filterState: presetFilterInfo.filterState };
  }
  return {};
};

// The only channels the streamingv2 delivery_csv_v2 endpoint maps to a platform; any other
// media_type would export with no platform prefix.
const DELIVERY_CSV_PREFIXES: string[] = ['streaming', 'audio', 'olv'];

export const supportsDeliveryCsv = (prefix: string): boolean =>
  DELIVERY_CSV_PREFIXES.includes(prefix);

// Query params for delivery_csv_v2. The endpoint prefers filter_id over filter_state.
export const buildDeliveryCsvParams = ({
  company,
  dates,
  prefix,
  filterID,
  filterState
}: {
  company: string;
  dates: { start: string; end: string };
  prefix: string;
  filterID?: number;
  filterState?: FilterPaneState;
}) => ({
  company,
  start: dates.start,
  end: dates.end,
  media_type: prefix,
  filter_id: filterID,
  filter_state: filterState ? JSON.stringify(filterState) : undefined
});
